import React, { useState, useEffect, useMemo } from "react";
import {
  Package,
  MapPin,
  Truck,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Clock,
  ShoppingBag,
  ArrowRight,
  Mail,
  Phone,
  MessageCircle,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  Globe,
  MessageSquare,
} from "lucide-react";
import TikTokIcon from '@/components/icons/TikTokIcon';
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useAuth, useCustomer } from "@/app/providers";
import { OrderRepository } from "@/services/firebase/order-repository";
import { OrgaConfigurationRepository } from "@/services/firebase/orga-configuration-repository";
import { SavChatRepository } from "@/services/firebase/sav-chat-repository";
import { FirebaseOrder, OrgaConfiguration, ChatReason, SavChat, ChatStatus } from "@/models";
import SeoTags from '@/components/shared/SeoTags';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { CreateSavChatModal } from '../components/CreateSavChatModal';
import { CustomerSavChat } from '../components/CustomerSavChat';
import { OrderChatBadge } from '../components/OrderChatBadge';
import { Button, Card, CardBody, StatusBadge } from '@/components/ui';
import type { StatusType } from '@/components/ui';


type FilterPeriod = 'all' | 'recent' | 'last7days' | 'last30days' | 'older';

interface OrderSupportSectionProps {
  organizationId: string;
  order: FirebaseOrder;
  orderChats: Map<string, SavChat[]>;
  onOpenChat: (order: FirebaseOrder) => void;
  onOpenExistingChat: (chatId: string) => void;
  /** Config préchargée (évite le flash au dépliage) */
  config?: OrgaConfiguration | null;
  configLoading?: boolean;
}

const OrderSupportSection: React.FC<OrderSupportSectionProps> = ({
  organizationId,
  order,
  orderChats,
  onOpenChat,
  onOpenExistingChat,
  config: configProp = null,
  configLoading = false,
}) => {
  const [configFallback, setConfigFallback] = useState<OrgaConfiguration | null>(null);
  const { t } = useTranslation();

  const config = configProp ?? configFallback;

  // Fallback uniquement si pas encore préchargé
  useEffect(() => {
    if (configProp || !organizationId) return;

    let cancelled = false;
    const fetchConfig = async () => {
      try {
        const data = await OrgaConfigurationRepository.getByOrganizationId(organizationId);
        if (!cancelled) setConfigFallback(data);
      } catch (err) {
        console.error("Error fetching orga config:", err);
      }
    };
    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, [organizationId, configProp]);

  // Combine SAV contacts (new dynamic list) with legacy fallbacks if list is empty
  const savContacts = useMemo(() => {
    if (!config) return [];

    const contacts = [...(config.afterSalesService?.contacts || [])];

    // If no dynamic contacts, try legacy fields
    if (contacts.length === 0) {
      // Use any cast to access potentially existing legacy fields during migration
      const legacySav = config.afterSalesService as any;

      if (legacySav?.email || config.email) {
        contacts.push({ type: 'email' as const, value: legacySav?.email || config.email || '', label: t('customerOrders.contact.email') });
      }
      if (legacySav?.phone || config.phone) {
        contacts.push({ type: 'phone' as const, value: legacySav?.phone || config.phone || '', label: t('customerOrders.contact.phone') });
      }
      if (legacySav?.whatsapp || config.whatsappNumber) {
        contacts.push({ type: 'whatsapp' as const, value: legacySav?.whatsapp || config.whatsappNumber || '', label: t('customerOrders.contact.whatsapp') });
      }
    }

    return contacts;
  }, [config, t]);

  if (!config) {
    if (configLoading) {
      return (
        <div className="mt-4 pt-4 border-t border-border-light animate-pulse">
          <div className="h-4 w-28 bg-background-tertiary rounded mb-3" />
          <div className="h-3 w-full max-w-sm bg-background-tertiary rounded mb-4" />
          <div className="flex gap-2">
            <div className="h-8 w-36 bg-background-tertiary rounded-lg" />
            <div className="h-8 w-28 bg-background-tertiary rounded-lg" />
          </div>
        </div>
      );
    }
    return null;
  }



  // Helper to determine contact link
  const getContactLink = (type: string, value: string) => {
    switch (type) {
      case 'email':
        return `mailto:${value}`;
      case 'phone':
        return `tel:${value}`;
      case 'whatsapp':
        return `https://wa.me/${value.replace(/\+/g, '').replace(/\s/g, '')}`;
      default:
        return '#';
    }
  };

  // Helper to get contact icon
  const getContactIcon = (type: string) => {
    switch (type) {
      case 'email': return Mail;
      case 'phone': return Phone;
      case 'whatsapp': return MessageCircle;
      default: return ExternalLink;
    }
  };

  const getSocialIcon = (key: string) => {
    switch (key.toLowerCase()) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'tiktok': return TikTokIcon;
      case 'linkedin': return Linkedin;
      case 'twitter': return Twitter;
      case 'youtube': return Youtube;
      case 'website': return Globe;
      case 'pinterest': return Globe;
      case 'snapchat': return MessageCircle;
      default: return ExternalLink;
    }
  };


  const hasSocials = config.instagramPage || config.facebookPage || (config.socials && Object.keys(config.socials).length > 0);
  const chats = orderChats.get(order.id!) || [];
  const activeChat = chats.find(c => c.status !== ChatStatus.CLOSED);
  const closedChats = chats.filter(c => c.status === ChatStatus.CLOSED);
  const savChatEnabled = config.afterSalesService?.isSavChatEnabled ?? false;

  return (
    <div className="mt-6 pt-6 border-t border-border-light">
      {/* Section Aide / Contact */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          {t('customerOrders.support.needHelp', 'Need help?')}
        </h4>
        <p className="text-sm text-foreground-secondary mb-4">
          {t('customerOrders.support.contactDescription', 'If you have any questions or issues, please contact Cashlive customer support:')}
        </p>

        {/* Boutons de contact - Discussion SAV + Contacts sur la même ligne */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-3">
            {/* Bouton Discussion SAV - Seulement si la fonctionnalité est activée */}
            {savChatEnabled && (
              activeChat ? (
                <button
                  onClick={() => onOpenExistingChat(activeChat.id)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 rounded-lg text-sm font-medium transition-all"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>{t('customerOrders.support.chatInProgress', 'Discussion en cours')}</span>
                  <span className="px-2 py-0.5 bg-primary text-white rounded-full text-xs">
                    {t('customerOrders.support.active', 'Active')}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => onOpenChat(order)}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg text-sm font-medium transition-all"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>{t('customerOrders.support.chatWithSupport', 'Chatter avec le SAV')}</span>
                </button>
              )
            )}

            {/* Autres contacts (After-Sales Service, Billing, etc.) */}
            {savContacts.map((contact, index) => {
              if (!contact.value) return null;
              const Icon = getContactIcon(contact.type);
              const link = getContactLink(contact.type, contact.value);

              let label = contact.label;
              const knownLabels = ['support_technique', 'service_client', 'service_apres_vente', 'facturation', 'retours', 'commercial', 'partenariat', 'presse', 'autre'];

              if (label && knownLabels.includes(label)) {
                label = t(`admin.configuration.contact.sav.labels.${label}`);
              }

              label = label || (contact.type === 'email' ? t('customerOrders.contact.email') : contact.type === 'phone' ? t('customerOrders.contact.phone') : t('customerOrders.contact.whatsapp'));

              const colorClass = contact.type === 'whatsapp'
                ? 'bg-green-50 hover:bg-green-100 text-green-700 border-green-200'
                : 'bg-background-secondary hover:bg-background-tertiary text-foreground-secondary border-border';

              return (
                <a
                  key={`sav-${index}`}
                  href={link}
                  target={contact.type === 'whatsapp' ? "_blank" : undefined}
                  rel={contact.type === 'whatsapp' ? "noopener noreferrer" : undefined}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${colorClass}`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </a>
              );
            })}
          </div>

          {/* Historique des discussions fermées */}
          {savChatEnabled && closedChats.length > 0 && (
            <div className="mt-2">
              <h5 className="text-xs font-semibold text-foreground-secondary mb-2 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t('customerOrders.support.history', 'Historique des échanges')}
              </h5>
              <div className="flex flex-wrap gap-2">
                {closedChats.map((chat) => (
                  <button
                    key={chat.id}
                    onClick={() => onOpenExistingChat(chat.id)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-surface border border-border rounded text-xs text-foreground hover:bg-background-secondary transition-colors"
                  >
                    <MessageSquare className="h-3 w-3 text-foreground-secondary" />
                    <span>
                      {t(`sav.reasons.${chat.reason}`, chat.reason)}
                      <span className="text-foreground-secondary ml-1">
                        ({new Date((chat.createdAt as any)?.toDate ? (chat.createdAt as any).toDate() : chat.createdAt).toLocaleDateString()})
                      </span>
                    </span>
                    <span className="w-1.5 h-1.5 bg-foreground-tertiary rounded-full ml-1" title={t('customerOrders.support.closed', 'Fermée')} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {savChatEnabled && (
          <p className="text-xs text-foreground-secondary mt-2">
            {t('customerOrders.support.responseTime', 'Notre équipe vous répondra dans les plus brefs délais')}
          </p>
        )}
      </div>

      {/* Socials Section */}
      {hasSocials && (
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">{t('admin.configuration.contact.socials.title')}</h4>
          <div className="flex flex-wrap gap-3">
            {/* Legacy Socials */}
            {config.instagramPage && (
              <a
                href={config.instagramPage.startsWith('http') ? config.instagramPage : `https://instagram.com/${config.instagramPage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-background-secondary hover:bg-background-tertiary text-foreground-secondary hover:text-foreground rounded-lg text-sm font-medium transition-colors border border-border"
              >
                <Instagram className="h-4 w-4" />
                <span>Instagram</span>
              </a>
            )}

            {config.facebookPage && (
              <a
                href={config.facebookPage.startsWith('http') ? config.facebookPage : `https://facebook.com/${config.facebookPage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-primary-10 hover:bg-primary-20 text-primary-dark rounded-lg text-sm font-medium transition-colors border border-primary-20"
              >
                <Facebook className="h-4 w-4" />
                <span>Facebook</span>
              </a>
            )}

            {/* Dynamic Socials */}
            {config.socials && Object.entries(config.socials).map(([key, value]) => {
              const Icon = getSocialIcon(key);
              return (
                <a
                  key={key}
                  href={value.startsWith('http') ? value : `https://${value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-background-secondary hover:bg-background-tertiary text-foreground-secondary rounded-lg text-sm font-medium transition-colors border border-border"
                >
                  <Icon className="h-4 w-4" />
                  <span className="capitalize">{key === 'website' ? t('customerOrders.support.website') : key}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Order Portal Link */}
      <div className="mt-6 pt-4 border-t border-border-light">
        <a
          href={`/orga/${organizationId}/order`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
        >
          <ShoppingBag className="h-4 w-4" />
          <span>Accéder à la boutique</span>
          <ArrowRight className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
};

const CustomerOrders: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { customerId, isLoading: customerLoading } = useCustomer();
  const [orders, setOrders] = useState<FirebaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');

  // SAV Chat states
  const [showCreateChatModal, setShowCreateChatModal] = useState(false);
  const [selectedOrderForChat, setSelectedOrderForChat] = useState<FirebaseOrder | null>(null);
  const [orderChats, setOrderChats] = useState<Map<string, SavChat[]>>(new Map()); // orderId -> SavChat[]
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [orgaConfigs, setOrgaConfigs] = useState<Map<string, OrgaConfiguration>>(new Map());
  const [orgaConfigsLoading, setOrgaConfigsLoading] = useState(false);

  // Load orders
  useEffect(() => {
    const loadOrders = async () => {
      if (!customerId || customerLoading) {
        if (!customerLoading && !customerId) setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const allOrders = await OrderRepository.getOrdersByCustomerId(customerId);

        // Filter only paid orders
        const paidOrders = allOrders.filter(
          (order) => (order as any).deliveryInfo?.paymentStatus === "paid"
        );

        // Sort by date desc
        paidOrders.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB.getTime() - dateA.getTime();
        });

        setOrders(paidOrders);
      } catch (error) {
        console.error("Error loading orders:", error);
        toast.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [customerId, customerLoading, t]);

  // Précharger les configs orga (section aide disponible immédiatement au dépliage)
  useEffect(() => {
    const loadConfigs = async () => {
      const orgIds = [...new Set(orders.map((o) => o.organizationId).filter(Boolean))];
      if (orgIds.length === 0) {
        setOrgaConfigs(new Map());
        return;
      }

      setOrgaConfigsLoading(true);
      try {
        const entries = await Promise.all(
          orgIds.map(async (orgId) => {
            try {
              const config = await OrgaConfigurationRepository.getByOrganizationId(orgId);
              return config ? ([orgId, config] as const) : null;
            } catch {
              return null;
            }
          }),
        );
        const map = new Map<string, OrgaConfiguration>();
        for (const entry of entries) {
          if (entry) map.set(entry[0], entry[1]);
        }
        setOrgaConfigs(map);
      } finally {
        setOrgaConfigsLoading(false);
      }
    };

    loadConfigs();
  }, [orders]);

  // Load existing chats for orders
  useEffect(() => {
    const loadChats = async () => {
      if (!customerId || orders.length === 0) return;

      try {
        const chatMap = new Map<string, SavChat[]>();

        await Promise.all(
          orders.map(async (order) => {
            if (!order.id) return;
            const existingChats = await SavChatRepository.getChatsByOrderId(order.id, customerId);
            if (existingChats && existingChats.length > 0) {
              chatMap.set(order.id, existingChats);
            }
          }),
        );

        setOrderChats(chatMap);
      } catch (error) {
        console.error("Error loading chats:", error);
      }
    };

    loadChats();
  }, [customerId, orders]);

  // Handle auto-open chat from notification
  useEffect(() => {
    const state = location.state as { openChatForOrderId?: string; openChatId?: string } | null;

    // Check if we have orders loaded and a request to open a chat
    if (state?.openChatForOrderId && orders.length > 0 && !showChatModal) {
      const targetOrderId = state.openChatForOrderId;
      const targetChatIdFromState = state.openChatId;
      const targetOrder = orders.find(o => o.id === targetOrderId);

      if (targetOrder) {
        let finalChatId = targetChatIdFromState;

        // If no specific ID provided, try fallback to first available chat
        if (!finalChatId) {
          const chats = orderChats.get(targetOrderId);
          if (chats && chats.length > 0) {
            finalChatId = chats[0].id;
          }
        }

        if (finalChatId) {
          setSelectedOrderForChat(targetOrder);
          setActiveChatId(finalChatId);
          setShowChatModal(true);
          // Clear state to avoid infinite reopen loop
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    }
  }, [location.state, orders, orderChats, showChatModal, navigate, location.pathname]);

  // Handle opening chat creation modal
  const handleOpenChatModal = (order: FirebaseOrder) => {
    setSelectedOrderForChat(order);
    setShowCreateChatModal(true);
  };

  // Handle creating a new chat
  const handleCreateChat = async (reason: ChatReason, reasonDetails: string, initialMessage: string) => {
    if (!selectedOrderForChat || !customerId) return;

    try {
      const orderAny = selectedOrderForChat as any;
      const chat = await SavChatRepository.createChat({
        orderId: selectedOrderForChat.id!,
        organizationId: selectedOrderForChat.organizationId,
        customerId,
        customerName: `${orderAny.orderInfo?.firstName || ''} ${orderAny.orderInfo?.lastName || ''}`.trim(),
        customerEmail: orderAny.orderInfo?.email || '',
        orderReference: selectedOrderForChat.id!.slice(-6).toUpperCase(),
        reason,
        reasonDetails,
        initialMessage,
      });

      // Update the chat map by appending new chat to existing list for this order (at the beginning)
      setOrderChats((prev) => {
        const newMap = new Map(prev);
        const existingChats = newMap.get(selectedOrderForChat.id!) || [];
        newMap.set(selectedOrderForChat.id!, [chat, ...existingChats]);
        return newMap;
      });

      // Open the chat
      setActiveChatId(chat.id);
      setShowChatModal(true);
      setShowCreateChatModal(false);
    } catch (error) {
      console.error("Error creating chat:", error);
      throw error;
    }
  };

  // Handle opening existing chat
  const handleOpenExistingChat = (chatId: string) => {
    const orderWithChat = orders.find(o => {
      const chats = orderChats.get(o.id!);
      return chats?.some(c => c.id === chatId);
    });

    if (orderWithChat) {
      setSelectedOrderForChat(orderWithChat); // Defined order for modal context
      setActiveChatId(chatId);
      setShowChatModal(true);
    }
  };

  const toggleExpand = (orderId: string) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) newSet.delete(orderId);
      else newSet.add(orderId);
      return newSet;
    });
  };



  // Filter orders by period
  const filteredOrders = useMemo(() => {
    const now = new Date();
    return orders.filter(order => {
      if (filterPeriod === 'all') return true;

      const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
      const diffTime = now.getTime() - orderDate.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);

      switch (filterPeriod) {
        case 'recent':
          return diffDays <= 7;
        case 'last7days':
          return diffDays <= 7;
        case 'last30days':
          return diffDays <= 30;
        case 'older':
          return diffDays > 30;
        default:
          return true;
      }
    });
  }, [orders, filterPeriod]);

  // Formatters
  const formatDate = (timestamp: any) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatPrice = (amount: number, currency: string = "EUR") => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getTotalAmount = (order: FirebaseOrder): number => {
    const orderAny = order as any;
    let productsTotal = 0;
    if (orderAny.products && Array.isArray(orderAny.products)) {
      productsTotal = orderAny.products.reduce((sum: number, product: any) => {
        const amount = parseFloat(product.amount || "0");
        return sum + amount;
      }, 0);
    }
    const shippingCost = orderAny.deliveryInfo?.shippingCost || 0;
    return Math.round((productsTotal + shippingCost) * 100);
  };

  // Render Helpers
  const getRefundInfo = (order: FirebaseOrder) => {
    const products = (order as any).products || [];
    if (!Array.isArray(products) || products.length === 0) {
      return { isFullyRefunded: false, isPartiallyRefunded: false, refundedCount: 0 };
    }
    const refundedCount = products.filter(
      (p: any) => p.paymentInfo?.status === "refunded_full" || p.paymentInfo?.status === "refunded_partial",
    ).length;
    return {
      isFullyRefunded: refundedCount === products.length && products.length > 0,
      isPartiallyRefunded: refundedCount > 0 && refundedCount < products.length,
      refundedCount,
    };
  };

  const getStatusInfo = (order: FirebaseOrder): {
    status: StatusType;
    label: string;
  } => {
    const orderAny = order as any;
    const prepStatus = orderAny.preparationStatus;
    const status = orderAny.status;
    const { isFullyRefunded, isPartiallyRefunded } = getRefundInfo(order);

    if (status === "cancelled") {
      return {
        status: "cancelled",
        label: t("customerOrders.paymentStatus.canceled") || "Annulée",
      };
    }
    if (isFullyRefunded) {
      return {
        status: "refunded_full",
        label: t("customerOrders.paymentStatus.refunded_full") || "Remboursée",
      };
    }
    if (isPartiallyRefunded) {
      return {
        status: "refunded_partial",
        label: t("customerOrders.details.partiallyRefunded") || "Partiellement remboursée",
      };
    }
    if (status === "delivered") {
      return {
        status: "ready",
        label: t("customerOrders.timeline.received") || "Reçue",
      };
    }
    if (orderAny.parcelGenerated?.trackingUrl || status === "shipped") {
      return {
        status: "ready",
        label: t("customerOrders.actions.trackPackage") || "Expédiée",
      };
    }
    if (prepStatus === "ready") {
      return {
        status: "ready",
        label: t("customerOrders.timeline.ready") || "Prête à être expédiée",
      };
    }
    if (prepStatus === "in_progress") {
      return {
        status: "in_progress",
        label: t("customerOrders.timeline.preparing") || "Préparation",
      };
    }

    return {
      status: "paid",
      label: t("customerOrders.order.status.paid") || "Payée",
    };
  };

  /** Étape 0..3 pour le stepper client */
  const getProgressStep = (order: FirebaseOrder): number => {
    const orderAny = order as any;
    if (orderAny.status === "delivered") return 3;
    if (orderAny.status === "shipped" || orderAny.parcelGenerated?.trackingUrl || orderAny.deliveryInfo?.trackingUrl) return 3;
    if (orderAny.preparationStatus === "ready") return 2;
    if (orderAny.preparationStatus === "in_progress") return 1;
    return 0; // payée
  };

  const { idorga } = useParams<{ idorga: string }>();
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-background-secondary flex items-center justify-center p-4">
        <SeoTags
          title={`Mes commandes | ${idorga ? "Espace de vente" : "CashLive"}`}
          description={`Connectez-vous pour accéder à vos commandes`}
          url={typeof window !== "undefined" ? window.location.href : ""}
          noIndex={true}
        />
        <div className="bg-surface rounded-2xl shadow-xl p-8 text-center max-w-md w-full">
          <div className="w-16 h-16 bg-success-10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="h-8 w-8 text-success" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">{t("customerOrders.title")}</h2>
          <p className="text-foreground-secondary mb-6">{t("customerOrders.loginRequired.description")}</p>
        </div>
      </div>
    );
  }

  const filterOptions: { id: FilterPeriod; label: string }[] = [
    { id: "all", label: t("common.all") || "Tous" },
    { id: "last7days", label: t("customerOrders.filters.last7days") },
    { id: "last30days", label: t("customerOrders.filters.last30days") },
    { id: "older", label: t("customerOrders.filters.older") },
  ];

  return (
    <div className="mt-4 max-w-2xl mx-auto">
      <SeoTags
        title={`Mes commandes | ${idorga ? "Espace de vente" : "CashLive"}`}
        description={`Historique et suivi de vos commandes`}
        url={typeof window !== "undefined" ? window.location.href : ""}
        noIndex={true}
      />

      {/* Header — aligné ManageOrganizations */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          {t("customerOrders.title")}
        </h1>
        <p className="text-foreground-secondary mt-1 text-sm">
          {loading
            ? t("common.loading")
            : t("customerOrders.order.number", { number: filteredOrders.length })}
        </p>
      </div>

      {/* Filtres — même pattern chips que ManageOrganizations */}
      <div className="mb-4 -mx-1 px-1 overflow-x-auto">
        <div className="inline-flex gap-1.5 flex-wrap">
          {filterOptions.map((filter) => (
            <button
              key={filter.id}
              type="button"
              onClick={() => setFilterPeriod(filter.id)}
              className={`
                inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap
                ${filterPeriod === filter.id
                  ? "bg-primary text-white shadow-md hover:shadow-lg"
                  : "bg-surface text-foreground-secondary hover:bg-background-tertiary hover:text-foreground border border-border"}
              `}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-border border-t-primary rounded-full animate-spin mb-4" />
          <p className="text-foreground-secondary">{t("common.loading")}</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card variant="default">
          <CardBody className="p-12 text-center">
            <ShoppingBag className="w-16 h-16 text-foreground-secondary mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("customerOrders.noOrders.empty")}
            </h3>
            {filterPeriod === "all" && (
              <p className="text-foreground-secondary">
                {t("customerOrders.empty.description")}
              </p>
            )}
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => {
            const totalAmount = getTotalAmount(order);
            const currency = (order as any).currency || "EUR";
            const isExpanded = expandedOrders.has(order.id || "");
            const statusInfo = getStatusInfo(order);
            const orderAny = order as any;
            const orderId = order.id || "";
            const { isFullyRefunded } = getRefundInfo(order);
            const isTerminal = isFullyRefunded || orderAny.status === "cancelled";
            const step = getProgressStep(order);
            const steps = [
              { key: "paid", label: t("customerOrders.order.status.paid") },
              { key: "preparing", label: t("customerOrders.order.status.preparing") },
              { key: "ready", label: t("customerOrders.order.status.ready") },
            ];
            if (orderAny.status === "delivered" || orderAny.parcelGenerated?.trackingUrl) {
              steps.push({
                key: "shipped",
                label: t("customerOrders.order.status.delivered") || "Expédiée",
              });
            }
            const maxStepIndex = Math.max(steps.length - 1, 1);
            const trackingUrl = orderAny.parcelGenerated?.trackingUrl;

            return (
              <Card key={orderId} variant="default" className="overflow-hidden hover:shadow-md transition-shadow">
                <CardBody className="!p-0">
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary-10 flex items-center justify-center flex-shrink-0">
                        <ShoppingBag className="h-4 w-4 text-primary" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <h3 className="font-semibold text-sm text-foreground truncate">
                                {order.organizationName || order.organizationId || "Commande"}
                              </h3>
                              <StatusBadge status={statusInfo.status} size="sm">
                                {statusInfo.label}
                              </StatusBadge>
                              {customerId && (
                                <OrderChatBadge orderId={orderId} customerId={customerId} />
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px] text-foreground-secondary flex-wrap">
                              <span>{formatDate(order.createdAt)}</span>
                              <span aria-hidden>•</span>
                              <span className="font-mono">#{orderId.slice(-6).toUpperCase()}</span>
                              <span aria-hidden>•</span>
                              <span>
                                {order.products?.length || 0}{" "}
                                {t("customerOrders.order.productsCount", {
                                  count: order.products?.length || 0,
                                })}
                              </span>
                            </div>
                          </div>

                          <p className="text-base font-bold text-foreground tabular-nums flex-shrink-0">
                            {formatPrice(totalAmount, currency)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {!isTerminal && (
                      <div className="mt-3 pt-3 border-t border-border-light">
                        <div className="relative flex justify-between px-0.5">
                          <div className="absolute left-2 right-2 top-2 h-px bg-border" />
                          <div
                            className="absolute left-2 top-2 h-px bg-success transition-all duration-500"
                            style={{
                              width: `calc(${(Math.min(step, maxStepIndex) / maxStepIndex) * 100}% - 0.5rem)`,
                              maxWidth: "calc(100% - 1rem)",
                            }}
                          />
                          {steps.map((s, index) => {
                            const done = step >= index;
                            return (
                              <div
                                key={s.key}
                                className="relative z-10 flex flex-col items-center flex-1 first:items-start last:items-end"
                              >
                                <div
                                  className={`w-4 h-4 rounded-full flex items-center justify-center border transition-colors ${
                                    done
                                      ? "bg-success border-success text-white"
                                      : "bg-surface border-border"
                                  }`}
                                >
                                  {done && <CheckCircle className="h-2.5 w-2.5" />}
                                </div>
                                <span
                                  className={`mt-1 text-[10px] font-medium leading-tight ${
                                    done ? "text-foreground" : "text-foreground-secondary"
                                  }`}
                                >
                                  {s.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="mt-3 pt-3 border-t border-border-light flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        {trackingUrl && !isTerminal ? (
                          <a
                            href={trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary-dark transition-colors"
                          >
                            <Truck className="h-3.5 w-3.5" />
                            {t("customerOrders.actions.trackPackage")}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : !isTerminal ? (
                          <span className="inline-flex items-center gap-1 text-xs text-foreground-secondary">
                            <Clock className="h-3.5 w-3.5" />
                            {orderAny.preparationStatus === "ready"
                              ? t("customerOrders.actions.waitingPickup")
                              : t("customerOrders.actions.waitingShipment")}
                          </span>
                        ) : null}
                      </div>

                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => toggleExpand(orderId)}
                        rightIcon={
                          isExpanded ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )
                        }
                      >
                        {isExpanded
                          ? t("customerOrders.actions.hideDetails")
                          : t("customerOrders.actions.showDetails")}
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="bg-background-secondary border-t border-border p-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                            <Package className="h-3.5 w-3.5" />
                            {t("customerOrders.details.products")} ({order.products?.length || 0})
                          </h4>
                          <Card variant="outlined">
                            <CardBody className="!p-0 divide-y divide-border-light">
                              {order.products?.map((product: any, idx: number) => {
                                const pStatus = product.paymentInfo?.status;
                                const productRefunded =
                                  pStatus === "refunded_full" || pStatus === "refunded_partial";

                                return (
                                  <div key={`${orderId}-p-${idx}`} className="p-2.5">
                                    <div className="flex justify-between items-start gap-2">
                                      <span className="text-xs font-medium text-foreground break-words flex-1 min-w-0">
                                        {product.model || product.reference}
                                      </span>
                                      <span className="text-xs text-foreground-secondary flex-shrink-0 tabular-nums">
                                        {formatPrice(
                                          Math.round(Number.parseFloat(product.amount || "0") * 100),
                                          currency,
                                        )}
                                      </span>
                                    </div>
                                    {productRefunded && (
                                      <div className="mt-1">
                                        <StatusBadge
                                          status={pStatus === "refunded_full" ? "refunded_full" : "refunded_partial"}
                                          size="sm"
                                        />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </CardBody>
                          </Card>
                        </div>

                        <div>
                          <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5" />
                            {t("customerOrders.details.delivery")}
                          </h4>
                          <Card variant="outlined">
                            <CardBody className="p-3">
                              {orderAny.deliveryInfo?.pickupPoint ? (
                                <div>
                                  <p className="font-medium text-foreground text-xs break-words">
                                    {orderAny.deliveryInfo.pickupPoint.name}
                                  </p>
                                  <p className="text-xs text-foreground-secondary mt-1 break-words">
                                    {orderAny.deliveryInfo.pickupPoint.address}
                                  </p>
                                  <p className="text-xs text-foreground-secondary">
                                    {orderAny.deliveryInfo.pickupPoint.postalCode}{" "}
                                    {orderAny.deliveryInfo.pickupPoint.city}
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <p className="font-medium text-foreground text-xs break-words">
                                    {orderAny.deliveryInfo?.firstName} {orderAny.deliveryInfo?.lastName}
                                  </p>
                                  <p className="text-xs text-foreground-secondary mt-1 break-words">
                                    {[orderAny.deliveryInfo?.houseNumber, orderAny.deliveryInfo?.address]
                                      .filter(Boolean)
                                      .join(" ")}
                                  </p>
                                  <p className="text-xs text-foreground-secondary">
                                    {[
                                      orderAny.deliveryInfo?.postalCode,
                                      orderAny.deliveryInfo?.city,
                                      orderAny.deliveryInfo?.country,
                                    ]
                                      .filter(Boolean)
                                      .join(" ")}
                                  </p>
                                </div>
                              )}

                              {orderAny.deliveryInfo?.shippingCost > 0 && (
                                <div className="mt-2 pt-2 border-t border-border-light flex justify-between items-center text-xs">
                                  <span className="text-foreground-secondary">
                                    {t("customerOrders.details.shippingCost")}
                                  </span>
                                  <span className="font-medium text-foreground tabular-nums">
                                    {orderAny.deliveryInfo.shippingCost.toFixed(2)} €
                                  </span>
                                </div>
                              )}
                            </CardBody>
                          </Card>
                        </div>
                      </div>

                      <OrderSupportSection
                        organizationId={order.organizationId}
                        order={order}
                        orderChats={orderChats}
                        onOpenChat={handleOpenChatModal}
                        onOpenExistingChat={handleOpenExistingChat}
                        config={orgaConfigs.get(order.organizationId) ?? null}
                        configLoading={orgaConfigsLoading && !orgaConfigs.has(order.organizationId)}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showCreateChatModal && selectedOrderForChat && (
        <CreateSavChatModal
          isOpen={showCreateChatModal}
          onClose={() => {
            setShowCreateChatModal(false);
            setSelectedOrderForChat(null);
          }}
          onSubmit={handleCreateChat}
          orderReference={selectedOrderForChat.id!.slice(-6).toUpperCase()}
        />
      )}

      {showChatModal && activeChatId && selectedOrderForChat && (
        <CustomerSavChat
          chatId={activeChatId}
          isOpen={showChatModal}
          onClose={() => {
            setShowChatModal(false);
            setActiveChatId(null);
          }}
          orderReference={selectedOrderForChat.id!.slice(-6).toUpperCase()}
        />
      )}
    </div>
  );
};

export default CustomerOrders;
